import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { fetchNotificationConfig, updateNotificationConfig } from '@/api/admin.api';
import type { NotificationConfig } from '@genealogy-app/shared';

type NotificationToggleKey = keyof Omit<NotificationConfig, 'recipientEmail'>;

const TOGGLES: { key: NotificationToggleKey; label: string; desc: string }[] = [
  { key: 'onBackupFailed',      label: 'Backup fehlgeschlagen',          desc: 'Bei jedem fehlgeschlagenen automatischen Backup.' },
  { key: 'onBackupSuccess',     label: 'Backup erfolgreich',             desc: 'Nach jedem erfolgreichen automatischen Backup.' },
  { key: 'onNewUnknownLogin',   label: 'Anmeldung von neuer IP',         desc: 'Wenn sich ein Benutzer von einer unbekannten IP-Adresse anmeldet.' },
  { key: 'onUserCreated',       label: 'Neuer Benutzer erstellt',        desc: 'Wenn ein neuer Benutzer angelegt oder eingeladen wird.' },
  { key: 'onRestorePerformed',  label: 'Datenbank-Wiederherstellung',    desc: 'Wenn ein Backup-Restore durchgeführt wird.' },
];

export function AdminNotificationsPage() {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [emailEditing, setEmailEditing] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'notifications'],
    queryFn: fetchNotificationConfig,
  });

  useEffect(() => {
    if (data && !emailEditing) setEmail(data.recipientEmail);
  }, [data, emailEditing]);

  const mutation = useMutation({
    mutationFn: updateNotificationConfig,
    onSuccess: (updated) => {
      queryClient.setQueryData(['admin', 'notifications'], updated);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleToggle = (key: NotificationToggleKey) => {
    if (!data) return;
    mutation.mutate({ [key]: !data[key] });
  };

  const handleEmailSave = () => {
    mutation.mutate(
      { recipientEmail: email },
      {
        onSuccess: () => {
          setEmailEditing(false);
          toast.success('E-Mail-Adresse gespeichert.');
        },
      },
    );
  };

  return (
    <div className="flex h-full flex-col bg-slate-50">
      <header className="flex h-14 shrink-0 items-center border-b border-slate-200 bg-white px-4 shadow-sm">
        <div className="flex items-center gap-2 sm:gap-4">
          <h1 className="text-base font-semibold text-slate-800 sm:text-lg">Benachrichtigungen</h1>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="mx-auto max-w-2xl space-y-6">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
            </div>
          ) : data && (
            <>
              {/* Empfänger-E-Mail */}
              <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
                <h2 className="mb-1 font-semibold text-slate-900">Empfänger-E-Mail</h2>
                <p className="mb-4 text-sm text-slate-500">
                  Admin-Benachrichtigungen werden an diese Adresse gesendet.
                  Muss SMTP konfiguriert sein.
                </p>
                {emailEditing ? (
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="admin@example.com"
                      className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleEmailSave}
                        disabled={mutation.isPending}
                        className="flex-1 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 sm:flex-none"
                      >
                        Speichern
                      </button>
                      <button
                        onClick={() => { setEmailEditing(false); setEmail(data.recipientEmail); }}
                        className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 sm:flex-none"
                      >
                        Abbrechen
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <span className={`flex-1 rounded-md px-3 py-2 text-sm ${data.recipientEmail ? 'bg-slate-50 text-slate-800' : 'italic text-slate-400'}`}>
                      {data.recipientEmail || 'Noch keine E-Mail-Adresse festgelegt'}
                    </span>
                    <button
                      onClick={() => setEmailEditing(true)}
                      className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                    >
                      Ändern
                    </button>
                  </div>
                )}
                {!data.recipientEmail && (
                  <p className="mt-2 text-xs text-amber-600">
                    ⚠ Ohne Empfänger-E-Mail werden keine Benachrichtigungen gesendet.
                  </p>
                )}
              </div>

              {/* Ereignis-Toggles */}
              <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-200 px-4 py-3">
                  <h2 className="font-semibold text-slate-900">Ereignisse</h2>
                  <p className="text-sm text-slate-500">Wähle, bei welchen Ereignissen du benachrichtigt werden möchtest.</p>
                </div>
                <div className="divide-y divide-slate-100">
                  {TOGGLES.map(({ key, label, desc }) => (
                    <div key={String(key)} className="flex items-center justify-between px-4 py-4">
                      <div className="mr-4">
                        <p className="font-medium text-slate-900">{label}</p>
                        <p className="text-sm text-slate-500">{desc}</p>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={data[key] as boolean}
                        onClick={() => handleToggle(key)}
                        disabled={mutation.isPending}
                        className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
                          data[key] ? 'bg-blue-600' : 'bg-slate-300'
                        }`}
                      >
                        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${data[key] ? 'translate-x-5' : 'translate-x-0'}`} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default AdminNotificationsPage;
